/** Home page section ids in DOM order for dock scroll helpers. */
export const WEB_DESIGNED_HOME_SECTION_ORDER = ['stays', 'activities', 'bundles', 'map', 'menu', 'contact'] as const;

/** True when dock scroll helpers should use home section anchors (vs generic page scroll). */
export function isWebdesignedHomePath(pathname: string): boolean {
  return pathname === '/' || pathname === '/webdesigned' || pathname === '/webdesigned/';
}

/** Cancel in-flight smooth scrolls before starting another (prevents mobile settle fights). */
function stopDocumentScrollMomentum(): void {
  if (typeof window === 'undefined') return;
  const y = window.scrollY;
  window.scrollTo({ top: y, behavior: 'auto' });
}

/** Scroll up/down: section-step on webdesigned home; otherwise smooth viewport-sized steps. */
export function dockScrollPage(pathname: string, direction: 'up' | 'down'): void {
  if (typeof window === 'undefined') return;
  stopDocumentScrollMomentum();
  if (isWebdesignedHomePath(pathname)) {
    dockScrollHomeBySectionStep(true, direction);
    return;
  }
  const delta = Math.round(window.innerHeight * 0.72);
  window.scrollBy({ top: direction === 'down' ? delta : -delta, behavior: 'smooth' });
}

export function dockScrollToHomeSection(isHomePage: boolean, sectionId: string): void {
  if (!isHomePage || typeof document === 'undefined') return;
  const section = document.getElementById(sectionId);
  if (!section) return;
  stopDocumentScrollMomentum();
  // scroll-margin-top on sections clears the sticky header.
  section.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function getHomeSectionTops(): { id: string; top: number }[] {
  if (typeof window === 'undefined' || typeof document === 'undefined') return [];
  return WEB_DESIGNED_HOME_SECTION_ORDER.map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => Boolean(el))
    .map((el) => ({ id: el.id, top: el.getBoundingClientRect().top + window.scrollY }))
    .sort((a, b) => a.top - b.top);
}

/** Sticky brand + nav clearance used when programmatically scrolling on mobile/desktop. */
const HOME_SCROLL_HEADER_OFFSET = 92;

/** Step between landing sections (respects sticky header offset). */
export function dockScrollHomeBySectionStep(isHomePage: boolean, direction: 'up' | 'down'): void {
  if (!isHomePage || typeof window === 'undefined') return;
  const sectionTops = getHomeSectionTops();
  if (sectionTops.length === 0) return;

  const anchorY = window.scrollY + HOME_SCROLL_HEADER_OFFSET;
  let currentIndex = sectionTops.findIndex((s, i) => {
    const nextTop = sectionTops[i + 1]?.top ?? Number.POSITIVE_INFINITY;
    return anchorY >= s.top - 2 && anchorY < nextTop - 2;
  });
  if (currentIndex < 0) {
    currentIndex = anchorY < sectionTops[0].top ? 0 : sectionTops.length - 1;
  }

  if (direction === 'down') {
    const targetIndex = Math.min(currentIndex + 1, sectionTops.length - 1);
    window.scrollTo({
      top: Math.max(0, sectionTops[targetIndex].top - HOME_SCROLL_HEADER_OFFSET),
      behavior: 'smooth',
    });
    return;
  }
  const targetIndex = Math.max(currentIndex - 1, 0);
  if (targetIndex === 0 && anchorY <= sectionTops[0].top + 8) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  window.scrollTo({
    top: Math.max(0, sectionTops[targetIndex].top - HOME_SCROLL_HEADER_OFFSET),
    behavior: 'smooth',
  });
}
